import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import QRCode from "qrcode";
import ShieldOutlined from "@mui/icons-material/ShieldOutlined";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import TotpCodeField from "./TotpCodeField";
import {
  TwoFactorSetup,
  TwoFactorStatus,
  disableTwoFactor,
  enableTwoFactor,
  getTwoFactorStatus,
  setupTwoFactor,
} from "../utils/api";

/** Backend detail arrives both as a string and as an object with locked_until. */
const describeError = (err: any): string => {
  const detail = err?.response?.data?.detail;
  if (!detail) return "Request failed";
  if (typeof detail === "string") return detail;
  if (detail.locked_until) {
    const until = new Date(detail.locked_until).toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `Too many invalid codes. Try again after ${until}`;
  }
  return detail.message || "Request failed";
};

const TwoFactorSection: React.FC<{
  onNotify?: (message: string, severity: "success" | "error") => void;
}> = ({ onNotify }) => {
  const theme = useTheme();
  const [status, setStatus] = useState<TwoFactorStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [setup, setSetup] = useState<TwoFactorSetup | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);

  const [disableOpen, setDisableOpen] = useState(false);
  const [disableCode, setDisableCode] = useState("");

  const notify = (message: string, severity: "success" | "error") =>
    onNotify?.(message, severity);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTwoFactorStatus();
      setStatus(res.data);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** The otpauth link is rendered to QR locally, so the secret never leaves. */
  useEffect(() => {
    if (!setup?.otpauth_url) {
      setQr(null);
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(setup.otpauth_url, {
      width: 320,
      margin: 1,
      errorCorrectionLevel: "M",
      color: { dark: "#0B1020", light: "#FFFFFF" },
    })
      .then((url) => {
        if (!cancelled) setQr(url);
      })
      .catch(() => {
        if (!cancelled) setQr(null);
      });
    return () => {
      cancelled = true;
    };
  }, [setup]);

  const startSetup = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await setupTwoFactor();
      setSetup(res.data);
      setCode("");
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async () => {
    setBusy(true);
    setError(null);
    try {
      await enableTwoFactor(code);
      notify("Two-factor authentication enabled", "success");
      setSetup(null);
      setCode("");
      await load();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const confirmDisable = async () => {
    setBusy(true);
    setError(null);
    try {
      await disableTwoFactor(disableCode);
      notify("Two-factor authentication disabled", "success");
      setDisableOpen(false);
      setDisableCode("");
      await load();
    } catch (err) {
      const message = describeError(err);
      setError(message);
      notify(message, "error");
    } finally {
      setBusy(false);
    }
  };

  const copySecret = () => {
    if (!setup) return;
    navigator.clipboard.writeText(setup.secret);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  const enabled = Boolean(status?.enabled);

  return (
    <Paper sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: "20px", mb: 2.5 }}>
      <Stack direction="row" spacing={1.75} alignItems="center" mb={2.5}>
        <Box
          sx={{
            width: 42,
            height: 42,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            borderRadius: "13px",
            color: enabled
              ? theme.palette.semanticText.success
              : theme.palette.accentText,
            bgcolor: alpha(
              enabled ? theme.palette.success.main : theme.palette.primary.main,
              0.11,
            ),
          }}
        >
          <ShieldOutlined fontSize="small" />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="h6" sx={{ fontSize: 17 }}>
              Two-factor authentication
            </Typography>
            {!loading && (
              <Chip
                size="small"
                label={enabled ? "Enabled" : "Disabled"}
                sx={{
                  fontWeight: 700,
                  color: enabled
                    ? theme.palette.semanticText.success
                    : theme.palette.text.secondary,
                  backgroundColor: alpha(
                    enabled ? theme.palette.success.main : "#667085",
                    0.12,
                  ),
                }}
              />
            )}
          </Stack>
          <Typography color="text.secondary" sx={{ fontSize: 13 }}>
            A one-time code from your authenticator on top of the password
          </Typography>
        </Box>
      </Stack>

      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {enabled && (
            <Stack spacing={2}>
              <Alert severity="success" icon={<ShieldOutlined fontSize="small" />}>
                Sign-in is protected by a code. If you lose access to the app, only an administrator can remove the protection — there are no backup codes.
              </Alert>
              <Box>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setDisableOpen(true)}
                >
                  Disable
                </Button>
              </Box>
            </Stack>
          )}

          {!enabled && !setup && (
            <Stack spacing={2}>
              {status?.pending_setup && (
                <Alert severity="warning">
                  Setup was started but never confirmed with a code. Start it again — the previous secret will be replaced.
                </Alert>
              )}
              <Typography color="text.secondary" sx={{ fontSize: 14 }}>
                You will need an authenticator app: Google Authenticator, 1Password, Aegis, or any other with TOTP support.
              </Typography>
              <Box>
                <Button
                  variant="contained"
                  onClick={startSetup}
                  disabled={busy}
                  startIcon={busy ? undefined : <ShieldOutlined />}
                >
                  {busy ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : status?.pending_setup ? (
                    "Set up again"
                  ) : (
                    "Enable"
                  )}
                </Button>
              </Box>
            </Stack>
          )}

          {!enabled && setup && (
            <Stack spacing={2.5}>
              <Typography sx={{ fontWeight: 650, fontSize: 14.5 }}>
                1. Scan the QR code with your app
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2.5}
                alignItems={{ xs: "stretch", sm: "flex-start" }}
              >
                <Box
                  sx={{
                    width: 200,
                    height: 200,
                    flexShrink: 0,
                    borderRadius: "16px",
                    overflow: "hidden",
                    display: "grid",
                    placeItems: "center",
                    backgroundColor: "#FFFFFF",
                    border: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {qr ? (
                    <img
                      src={qr}
                      alt="QR code for the authenticator app"
                      width={190}
                      height={190}
                    />
                  ) : (
                    <CircularProgress size={24} />
                  )}
                </Box>

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography color="text.secondary" sx={{ fontSize: 13, mb: 1 }}>
                    Cannot scan? Enter the key manually:
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      p: 1.25,
                      borderRadius: "12px",
                      border: `1px solid ${theme.palette.divider}`,
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? alpha("#FFFFFF", 0.04)
                          : alpha("#172033", 0.03),
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "monospace",
                        fontSize: 13.5,
                        letterSpacing: "0.06em",
                        wordBreak: "break-all",
                        flex: 1,
                      }}
                    >
                      {setup.secret}
                    </Typography>
                    <Tooltip title={copied ? "Copied" : "Copy key"}>
                      <IconButton
                        aria-label="Copy key"
                        size="small"
                        onClick={copySecret}
                      >
                        {copied ? (
                          <CheckRounded
                            sx={{
                              fontSize: 16,
                              color: theme.palette.semanticText.success,
                            }}
                          />
                        ) : (
                          <ContentCopyRounded sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography
                    color="text.secondary"
                    sx={{ fontSize: 12, mt: 1 }}
                  >
                    Issuer: {setup.issuer}
                  </Typography>
                </Box>
              </Stack>

              <Divider />

              <Typography sx={{ fontWeight: 650, fontSize: 14.5 }}>
                2. Enter the code from your app to confirm
              </Typography>

              <Box sx={{ maxWidth: 280 }}>
                <TotpCodeField
                  value={code}
                  onChange={setCode}
                  autoFocus
                  disabled={busy}
                  onEnter={() => code.length >= 6 && confirmEnable()}
                />
              </Box>

              <Stack direction="row" spacing={1.5}>
                <Button
                  variant="contained"
                  onClick={confirmEnable}
                  disabled={code.length < 6 || busy}
                >
                  {busy ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Confirm and enable"
                  )}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSetup(null);
                    setCode("");
                    setError(null);
                  }}
                  disabled={busy}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          )}
        </>
      )}

      <Dialog
        open={disableOpen}
        onClose={() => setDisableOpen(false)}
        fullWidth
        maxWidth="xs"
        disableScrollLock
      >
        <DialogTitle>Disable two-factor authentication</DialogTitle>
        <DialogContent>
          <Stack direction="row" spacing={1} alignItems="flex-start" mb={2.5}>
            <WarningAmberRounded color="error" fontSize="small" />
            <Typography sx={{ fontSize: 14 }}>
              Sign-in will be protected by the password alone. Enter a valid code from your app to confirm.
            </Typography>
          </Stack>
          <TotpCodeField
            value={disableCode}
            onChange={setDisableCode}
            autoFocus
            disabled={busy}
            onEnter={() => disableCode.length >= 6 && confirmDisable()}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" onClick={() => setDisableOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDisable}
            disabled={disableCode.length < 6 || busy}
          >
            Disable
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default TwoFactorSection;
